import { useState, useMemo, useRef } from 'react';

// Read-only price-fluctuation chart for a product's supplier price history.
// Self-contained SVG (no charting dependency). Prices are step functions — a
// price holds until the next recorded change — so the lines are drawn stepped.

const PERIODS = [
  { key: '1m',  label: '1M',  days: 30 },
  { key: '3m',  label: '3M',  days: 90 },
  { key: '6m',  label: '6M',  days: 182 },
  { key: '1y',  label: '1Y',  days: 365 },
  { key: 'all', label: 'All', days: null },
];

const COLORS = ['#2563eb', '#059669', '#d97706', '#7c3aed', '#dc2626', '#0891b2', '#db2777', '#65a30d'];

const VB_W = 720, VB_H = 250;
const PAD = { l: 52, r: 14, t: 14, b: 34 };
const PLOT_W = VB_W - PAD.l - PAD.r;
const PLOT_H = VB_H - PAD.t - PAD.b;

const DAY = 86400000;

function parseTs(s) {
  if (!s) return null;
  let t = s.includes('T') ? s : s.replace(' ', 'T');
  if (!/[Z+]/.test(t)) t += 'Z';           // D1 stores UTC without a zone marker
  const d = new Date(t);
  return isNaN(d.getTime()) ? null : d.getTime();
}

// Build per-supplier event lists: { supplier, events: [{t, p}], firstOld }
function buildSuppliers(history) {
  const bySupplier = {};
  for (const h of history || []) {
    const t = parseTs(h.changed_at);
    const p = Number(h.new_price);
    if (t == null || !isFinite(p)) continue;
    const key = h.supplier_name || 'Unknown';
    if (!bySupplier[key]) bySupplier[key] = [];
    bySupplier[key].push({ t, p, old: h.old_price != null ? Number(h.old_price) : null });
  }
  return Object.entries(bySupplier).map(([supplier, evs]) => {
    evs.sort((a, b) => a.t - b.t);
    return { supplier, events: evs, firstOld: evs[0]?.old ?? null };
  });
}

// Price in effect at time `t` for one supplier's sorted events.
function priceAt(sup, t) {
  let val = sup.firstOld != null ? sup.firstOld : (sup.events[0]?.p ?? null);
  for (const e of sup.events) {
    if (e.t <= t) val = e.p;
    else break;
  }
  return val;
}

function fmtTick(ts, spanDays) {
  const d = new Date(ts);
  if (spanDays <= 92) return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  return d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit', timeZone: 'UTC' });
}

export default function PriceHistoryChart({ history, fmt }) {
  const [period, setPeriod] = useState('1y');
  const [hidden, setHidden] = useState({});
  const [hover, setHover] = useState(null); // { t, x }
  const svgRef = useRef(null);

  const suppliers = useMemo(() => buildSuppliers(history), [history]);

  const model = useMemo(() => {
    if (!suppliers.length) return null;
    const now = Date.now();
    const earliest = Math.min(...suppliers.flatMap(s => s.events.map(e => e.t)));
    const pdef = PERIODS.find(p => p.key === period) || PERIODS[3];
    const start = pdef.days ? now - pdef.days * DAY : earliest;
    const end = now;
    const spanDays = (end - start) / DAY;

    // Visible suppliers → stepped point series within [start, end]
    const lines = suppliers
      .filter(s => !hidden[s.supplier])
      .map((s, i) => {
        const startP = priceAt(s, start);
        const within = s.events.filter(e => e.t > start && e.t <= end);
        const pts = [];
        if (startP != null) pts.push({ t: start, p: startP });
        for (const e of within) pts.push({ t: e.t, p: e.p });
        const endP = priceAt(s, end);
        if (endP != null) pts.push({ t: end, p: endP });
        const color = COLORS[suppliers.indexOf(s) % COLORS.length];
        return { supplier: s.supplier, color, pts, startP, endP };
      })
      .filter(l => l.pts.length > 0);

    const allP = lines.flatMap(l => l.pts.map(p => p.p));
    if (!allP.length) return { start, end, spanDays, lines: [], minP: 0, maxP: 0 };
    let minP = Math.min(...allP), maxP = Math.max(...allP);
    if (minP === maxP) { const b = Math.max(1, Math.abs(minP) * 0.1); minP -= b; maxP += b; }
    else { const b = (maxP - minP) * 0.14; minP -= b; maxP += b; }
    if (minP < 0) minP = 0;
    return { start, end, spanDays, lines, minP, maxP };
  }, [suppliers, period, hidden]);

  if (!suppliers.length) return null;

  const x = (t) => PAD.l + ((t - model.start) / (model.end - model.start || 1)) * PLOT_W;
  const y = (p) => PAD.t + (1 - (p - model.minP) / (model.maxP - model.minP || 1)) * PLOT_H;

  // Stepped path (stepAfter): hold price until the next change
  const pathFor = (pts) => {
    if (!pts.length) return '';
    let d = `M ${x(pts[0].t).toFixed(1)} ${y(pts[0].p).toFixed(1)}`;
    for (let i = 1; i < pts.length; i++) {
      d += ` L ${x(pts[i].t).toFixed(1)} ${y(pts[i - 1].p).toFixed(1)}`;
      d += ` L ${x(pts[i].t).toFixed(1)} ${y(pts[i].p).toFixed(1)}`;
    }
    return d;
  };

  const yTicks = 4;
  const gridVals = Array.from({ length: yTicks + 1 }, (_, i) => model.minP + (i / yTicks) * (model.maxP - model.minP));
  const xTickN = model.spanDays <= 92 ? 4 : 5;
  const xTicks = Array.from({ length: xTickN + 1 }, (_, i) => model.start + (i / xTickN) * (model.end - model.start));

  const onMove = (e) => {
    const svg = svgRef.current; if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const vbX = ((e.clientX - rect.left) / rect.width) * VB_W;
    if (vbX < PAD.l || vbX > VB_W - PAD.r) { setHover(null); return; }
    const t = model.start + ((vbX - PAD.l) / PLOT_W) * (model.end - model.start);
    setHover({ t, x: vbX });
  };

  const hoverVals = hover
    ? model.lines.map(l => {
        const sup = suppliers.find(s => s.supplier === l.supplier);
        return { supplier: l.supplier, color: l.color, p: priceAt(sup, hover.t) };
      }).filter(v => v.p != null)
    : [];

  return (
    <div className="mb-3">
      {/* Period selector */}
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="text-[8px] font-bold uppercase text-gray-400">Price over time</div>
        <div className="flex gap-0.5">
          {PERIODS.map(p => (
            <button key={p.key} onClick={() => setPeriod(p.key)}
              className={`px-2 py-0.5 text-[8px] font-bold rounded transition-colors ${
                period === p.key ? 'bg-navy text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {model.lines.length === 0 ? (
        <div className="text-[9px] text-gray-400 border border-dashed border-gray-200 rounded py-8 text-center">
          No price changes in this period.
        </div>
      ) : (
        <div className="border border-gray-100 rounded-lg bg-white p-1">
          <svg ref={svgRef} viewBox={`0 0 ${VB_W} ${VB_H}`} className="w-full" style={{ height: 'auto' }}
            onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
            {/* Y gridlines + labels */}
            {gridVals.map((v, i) => (
              <g key={i}>
                <line x1={PAD.l} y1={y(v)} x2={VB_W - PAD.r} y2={y(v)} stroke="#eef0f3" strokeWidth="1" />
                <text x={PAD.l - 6} y={y(v) + 3} textAnchor="end" fontSize="9" fill="#9ca3af">{fmt(v)}</text>
              </g>
            ))}
            {/* X ticks */}
            {xTicks.map((t, i) => (
              <text key={i} x={x(t)} y={VB_H - PAD.b + 16} textAnchor="middle" fontSize="9" fill="#9ca3af">
                {fmtTick(t, model.spanDays)}
              </text>
            ))}
            {/* Lines */}
            {model.lines.map(l => (
              <g key={l.supplier}>
                <path d={pathFor(l.pts)} fill="none" stroke={l.color} strokeWidth="2" strokeLinejoin="round" />
                {/* Dots only on actual change events (not the synthetic window-start/now anchors) */}
                {l.pts.map((pt, idx) => (idx > 0 && idx < l.pts.length - 1)
                  ? <circle key={idx} cx={x(pt.t)} cy={y(pt.p)} r="2.5" fill={l.color} />
                  : null)}
              </g>
            ))}
            {/* Hover guide */}
            {hover && (
              <line x1={hover.x} y1={PAD.t} x2={hover.x} y2={VB_H - PAD.b} stroke="#cbd5e1" strokeWidth="1" strokeDasharray="3 3" />
            )}
            {hover && hoverVals.map((v, i) => (
              <circle key={i} cx={hover.x} cy={y(v.p)} r="3.5" fill="#fff" stroke={v.color} strokeWidth="2" />
            ))}
          </svg>

          {/* Hover tooltip */}
          {hover && hoverVals.length > 0 && (
            <div className="text-[9px] text-gray-600 px-2 pb-1 flex items-center gap-3 flex-wrap">
              <span className="font-semibold text-gray-400">{new Date(hover.t).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })}</span>
              {hoverVals.map((v, i) => (
                <span key={i} className="inline-flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full" style={{ background: v.color }} />
                  <span className="font-semibold text-gray-700">{fmt(v.p)}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Legend / per-supplier summary (click to toggle) */}
      <div className="flex flex-wrap gap-2 mt-2">
        {suppliers.map((s, i) => {
          const color = COLORS[i % COLORS.length];
          const isHidden = !!hidden[s.supplier];
          const line = model.lines.find(l => l.supplier === s.supplier);
          const delta = line ? (line.endP - line.startP) : 0;
          const pct = line && line.startP ? (delta / line.startP) * 100 : 0;
          return (
            <button key={s.supplier}
              onClick={() => setHidden(h => ({ ...h, [s.supplier]: !h[s.supplier] }))}
              className={`flex items-center gap-1.5 px-2 py-1 rounded border text-[9px] transition-opacity ${isHidden ? 'opacity-40 border-gray-200' : 'border-gray-200'}`}
              title={isHidden ? 'Show' : 'Hide'}>
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: color }} />
              <span className="font-semibold text-gray-700">{s.supplier}</span>
              {line && (
                <>
                  <span className="text-gray-500">{fmt(line.endP)}</span>
                  {Math.abs(delta) > 0.005 && (
                    <span className={`font-bold ${delta > 0 ? 'text-red-500' : 'text-emerald-600'}`}>
                      {delta > 0 ? '▲' : '▼'} {Math.abs(pct).toFixed(1)}%
                    </span>
                  )}
                </>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
